<template>
  <basic-modal
    v-model="isSupportModalOpen"
    persistent
    :close-disabled="isSubmitting"
    width="clamp(550px, 80vw, 700px)"
    @show="onModalShow"
    @hide="resetSupportForm"
  >
    <template #title-container>
      <div class="flex items-center gap-sm">
        <lucide-icon
          name="help-circle"
          color="white"
          size="28px"
          aria-hidden="true"
        />
        <h5 class="text-20 text-white text-font-semibold">Contact Support</h5>
      </div>
    </template>

    <template #content>
      <q-card
        flat
        class="q-pa-none"
      >
        <q-card-section class="border-bottom">
          <h6 class="q-mb-xxs text-16 text-primary text-font-semibold">Need help?</h6>

          <p class="q-mb-none text-12 text-secondary">
            Fill out the form below and our support team will get back to you as soon as possible.
          </p>
        </q-card-section>

        <q-form class="support-form q-pa-md">
          <div class="q-mb-md">
            <label
              for="support-topic"
              class="q-mb-xxs text-12 text-secondary text-font-medium"
            >
              What do you need help with?
              <span class="text-brand">*</span>
            </label>

            <q-select
              ref="topicSelectRef"
              v-model="supportFormData.topic"
              for="support-topic"
              v-bind="FIELD_CHROME.form"
              :options="SUPPORT_TOPICS"
              option-value="value"
              option-label="label"
              :disable="isSubmitting"
              emit-value
              dense
              dropdown-icon="keyboard_arrow_down"
              map-options
              class="support-select"
              @popup-hide="onFocusMessage"
            >
              <template #prepend>
                <span
                  v-if="!supportFormData.topic"
                  class="text-14 text-secondary"
                >
                  Choose an option
                </span>
              </template>
            </q-select>
          </div>

          <div>
            <label
              for="support-message"
              class="q-mb-xxs text-12 text-secondary text-font-medium"
            >
              Message
              <span class="text-brand">*</span>
            </label>

            <q-input
              ref="messageInputRef"
              v-model="supportFormData.message"
              for="support-message"
              type="textarea"
              v-bind="FIELD_CHROME.form"
              placeholder="Describe your issue or question in detail..."
              :disable="isSubmitting"
              :maxlength="MAX_LENGTH.LONG_TEXT"
              rows="6"
              class="support-textarea"
            />
          </div>
        </q-form>
      </q-card>
    </template>

    <template #footer>
      <q-btn
        label="Cancel"
        class="cancel-btn sm-radius"
        color="brand-light"
        outline
        padding="0.5rem 1rem"
        no-caps
        :disable="isSubmitting"
        @click="closeSupportModal"
      />

      <q-btn
        label="Submit"
        color="brand-dark"
        padding="0.5rem 1.5rem"
        class="sm-radius"
        unelevated
        no-caps
        :loading="isSubmitting"
        :disable="!isFormValid || isSubmitting"
        @click="onSubmit"
      />
    </template>
  </basic-modal>
</template>

<script setup>
  import { ref, computed, nextTick } from 'vue'

  import {
    useSubmitSupportMutation,
    useSupportModal,
  } from 'src/composables/app/mutation/useSubmitSupportMutation'

  import { FIELD_CHROME, MAX_LENGTH } from 'src/services/consts/app.const'
  import { SUPPORT_TOPICS } from 'src/services/consts/support.const'

  const { submitSupport, isSubmitting } = useSubmitSupportMutation()
  const { isSupportModalOpen, supportFormData, closeSupportModal, resetSupportForm } =
    useSupportModal()

  const topicSelectRef = ref(null)
  const messageInputRef = ref(null)

  const isFormValid = computed(
    () => supportFormData.value.topic && supportFormData.value.message?.trim()
  )

  async function onModalShow() {
    await nextTick()
    if (!supportFormData.value.topic) {
      topicSelectRef.value?.showPopup()
    }
  }

  function onFocusMessage() {
    messageInputRef.value?.focus()
  }

  async function onSubmit() {
    if (!isFormValid.value) return

    const result = await submitSupport({
      topic: supportFormData.value.topic,
      message: supportFormData.value.message.trim(),
    })

    if (result) {
      closeSupportModal()
    }
  }
</script>

<style lang="scss">
  .support-form {
    .q-select {
      span {
        color: $primary;
      }
    }
    .support-textarea {
      textarea {
        white-space: pre-wrap;
        font-family: inherit;
        padding: 0.875rem 0.25rem;
        resize: vertical;
      }
    }
  }
</style>
